import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Text, Card, Button, ActivityIndicator, DataTable, Menu } from 'react-native-paper';
import PeriodoChip from '../../components/PeriodoChip';
import { Ionicons } from '@expo/vector-icons';
import { formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import { exportService } from '../../services/exportService';

const PERIODOS = [
  { id: 'hoy', label: 'Hoy' }, { id: 'esta_semana', label: 'Esta Semana' },
  { id: 'este_mes', label: 'Este Mes' }, { id: 'ultimo_trimestre', label: 'Último Trimestre' },
];

const ProgressBar = ({ value, max, color }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <View style={styles.progressBg}>
      <View style={[styles.progressFill, { width: `${Math.min(pct, 100)}%`, backgroundColor: color }]} />
    </View>
  );
};

const ReporteEficienciaScreen = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState('este_mes');

  useEffect(() => { cargarReporte(); }, []);

  const cargarReporte = async (periodo = periodoActivo) => {
    try {
      setLoading(true);
      const response = await api.get(`/reportes/eficiencia-operativa?periodo=${periodo}`);
      setData(response);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el reporte');
    } finally { setLoading(false); }
  };

  const cambiarPeriodo = (p) => { setPeriodoActivo(p); cargarReporte(p); };

  const exportarDatos = async (formato) => {
    setMenuVisible(false);
    if (!data) return;

    const devs = data.devoluciones?.lista || [];
    if (formato === 'excel' || formato === 'csv' || formato === 'pdf') {
      const headers = ['Fecha', 'Venta', 'Cliente', 'Autorizado Por', 'Motivo', 'Monto', 'Estado'];
      const exportData = devs.map(d => ({
        'Fecha': d.fecha, 'Venta': d.venta_id, 'Cliente': d.cliente_nombre || '-',
        'Autorizado Por': d.autorizado_por || '-', 'Motivo': d.motivo || '-',
        'Monto': parseFloat(d.monto_devolucion || 0).toFixed(2), 'Estado': d.estado || '-',
      }));

      // Also add summary data
      const summaryData = [
        { 'Fecha': '--- RESUMEN BODEGA ---', 'Venta': '', 'Cliente': '', 'Autorizado Por': '', 'Motivo': '', 'Monto': '', 'Estado': '' },
        { 'Fecha': 'Total Pedidos', 'Venta': data.bodega?.total_pedidos, 'Cliente': '', 'Autorizado Por': '', 'Motivo': `Tasa: ${data.bodega?.tasa_preparacion}%`, 'Monto': '', 'Estado': '' },
        { 'Fecha': '--- RESUMEN ENTREGAS ---', 'Venta': '', 'Cliente': '', 'Autorizado Por': '', 'Motivo': '', 'Monto': '', 'Estado': '' },
        { 'Fecha': 'Total Entregadas', 'Venta': data.entregas?.total_entregadas, 'Cliente': '', 'Autorizado Por': '', 'Motivo': `Tasa: ${data.entregas?.tasa_entrega}%`, 'Monto': '', 'Estado': '' },
        { 'Fecha': '--- DEVOLUCIONES ---', 'Venta': '', 'Cliente': '', 'Autorizado Por': '', 'Motivo': '', 'Monto': '', 'Estado': '' },
        ...exportData
      ];

      await exportService.exportData(formato, summaryData, 'eficiencia_operativa', headers, 'Reporte de Eficiencia Operativa');
    }
  };

  const bodega = data?.bodega || {};
  const entregas = data?.entregas || {};
  const devs = data?.devoluciones || {};

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Eficiencia Operativa</Text>
          <Text style={styles.subtitle}>Bodega, Entregas y Devoluciones</Text>
        </View>
        <Menu visible={menuVisible} onDismiss={() => setMenuVisible(false)}
          anchor={<Button icon="download" mode="outlined" onPress={() => setMenuVisible(true)} disabled={loading}>Exportar</Button>}>
          <Menu.Item title="Excel" onPress={() => exportarDatos('excel')} />
          <Menu.Item title="PDF" onPress={() => exportarDatos('pdf')} />
          <Menu.Item title="CSV" onPress={() => exportarDatos('csv')} />
        </Menu>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtrosContainer} contentContainerStyle={styles.filtrosContent}>
        <View style={styles.filtros}>
          {PERIODOS.map(p => (
            <PeriodoChip
              key={p.id}
              active={periodoActivo === p.id}
              onPress={() => !loading && cambiarPeriodo(p.id)}
            >
              {p.label}
            </PeriodoChip>
          ))}
        </View>
      </ScrollView>

      {loading ? (
        <View style={styles.loader}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={{ marginTop: 10, color: '#666' }}>Generando reporte...</Text>
        </View>
      ) : (
      <ScrollView contentContainerStyle={{ padding: 16 }}>
        {/* Bodega */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.cardHeader}>
              <Ionicons name="file-tray-stacked" size={20} color="#FF9800" />
              <Text style={styles.cardTitle}>Eficiencia de Bodega</Text>
            </View>
            <View style={styles.metricRow}>
              <Text style={styles.metricLabel}>Pedidos Procesados</Text>
              <Text style={styles.metricValue}>{bodega.pedidos_preparados} / {bodega.total_pedidos}</Text>
            </View>
            <ProgressBar value={bodega.pedidos_preparados} max={bodega.total_pedidos} color="#FF9800" />
            <View style={styles.metricRow}>
              <Text style={styles.metricLabel}>Tasa de Preparación</Text>
              <Text style={[styles.metricValue, { color: parseFloat(bodega.tasa_preparacion) >= 80 ? '#28a745' : '#dc3545' }]}>
                {bodega.tasa_preparacion}%
              </Text>
            </View>
            <View style={styles.metricRow}>
              <Text style={styles.metricLabel}>Pendientes en Bodega</Text>
              <Text style={[styles.metricValue, { color: '#dc3545' }]}>{bodega.pendientes}</Text>
            </View>
          </Card.Content>
        </Card>

        {/* Entregas */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.cardHeader}>
              <Ionicons name="car" size={20} color="#28a745" />
              <Text style={styles.cardTitle}>Eficiencia de Entregas</Text>
            </View>
            <View style={styles.metricRow}>
              <Text style={styles.metricLabel}>Entregadas</Text>
              <Text style={styles.metricValue}>{entregas.total_entregadas}</Text>
            </View>
            <ProgressBar value={entregas.total_entregadas} max={entregas.total_entregadas + entregas.en_ruta} color="#28a745" />
            <View style={styles.metricRow}>
              <Text style={styles.metricLabel}>Tasa de Entrega</Text>
              <Text style={[styles.metricValue, { color: parseFloat(entregas.tasa_entrega) >= 80 ? '#28a745' : '#ffc107' }]}>
                {entregas.tasa_entrega}%
              </Text>
            </View>
            <View style={styles.metricRow}>
              <Text style={styles.metricLabel}>En Ruta</Text>
              <Text style={[styles.metricValue, { color: '#ffc107' }]}>{entregas.en_ruta}</Text>
            </View>
            <View style={styles.metricRow}>
              <Text style={styles.metricLabel}>Con Firma del Cliente</Text>
              <Text style={styles.metricValue}>{entregas.con_firma}</Text>
            </View>
          </Card.Content>
        </Card>

        {/* Devoluciones */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.cardHeader}>
              <Ionicons name="return-down-back" size={20} color="#dc3545" />
              <Text style={styles.cardTitle}>Devoluciones</Text>
            </View>
            <View style={styles.devResumen}>
              <View style={styles.devItem}>
                <Text style={[styles.devValue, { color: '#dc3545' }]}>{devs.total_devoluciones}</Text>
                <Text style={styles.devLabel}>Total</Text>
              </View>
              <View style={styles.devItem}>
                <Text style={[styles.devValue, { color: '#dc3545' }]}>{formatCurrency(devs.monto_devoluciones)}</Text>
                <Text style={styles.devLabel}>Monto</Text>
              </View>
              <View style={styles.devItem}>
                <Text style={[styles.devValue, { color: parseFloat(devs.tasa_devolucion) > 5 ? '#dc3545' : '#28a745' }]}>
                  {devs.tasa_devolucion}%
                </Text>
                <Text style={styles.devLabel}>Tasa</Text>
              </View>
            </View>

            {(devs.lista || []).length > 0 && (
              <ScrollView horizontal style={{ marginTop: 12 }}>
                <DataTable style={{ minWidth: 600 }}>
                  <DataTable.Header>
                    <DataTable.Title style={{ minWidth: 80 }}>Fecha</DataTable.Title>
                    <DataTable.Title style={{ minWidth: 120 }}>Cliente</DataTable.Title>
                    <DataTable.Title style={{ minWidth: 120 }}>Autorizado Por</DataTable.Title>
                    <DataTable.Title style={{ minWidth: 140 }}>Motivo</DataTable.Title>
                    <DataTable.Title numeric style={{ minWidth: 90 }}>Monto</DataTable.Title>
                  </DataTable.Header>
                  {(devs.lista || []).map((d, idx) => (
                    <DataTable.Row key={idx}>
                      <DataTable.Cell style={{ minWidth: 80 }}>{d.fecha}</DataTable.Cell>
                      <DataTable.Cell style={{ minWidth: 120 }}>{d.cliente_nombre || '-'}</DataTable.Cell>
                      <DataTable.Cell style={{ minWidth: 120 }}>{d.autorizado_por || '-'}</DataTable.Cell>
                      <DataTable.Cell style={{ minWidth: 140 }}>{d.motivo || '-'}</DataTable.Cell>
                      <DataTable.Cell numeric style={{ minWidth: 90 }}>{formatCurrency(d.monto_devolucion)}</DataTable.Cell>
                    </DataTable.Row>
                  ))}
                </DataTable>
              </ScrollView>
            )}
          </Card.Content>
        </Card>
        <View style={{ height: 20 }} />
      </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: 16, backgroundColor: '#fff', elevation: 2 },
  title: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  subtitle: { fontSize: 14, color: '#666', marginTop: 2 },
  filtrosContainer: { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee', height: 56, flexGrow: 0, flexShrink: 0 },
  filtrosContent: { alignItems: 'center', paddingVertical: 10 },
  filtros: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16 },
  chip: { marginRight: 8, backgroundColor: '#f0f0f0' },
  chipActivo: { backgroundColor: '#4CAF50' },
  chipTexto: { color: '#333' },
  chipTextoActivo: { color: '#fff', fontWeight: '600' },
  card: { marginBottom: 12, elevation: 2, borderRadius: 8 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  cardTitle: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  metricRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
  metricLabel: { fontSize: 14, color: '#666' },
  metricValue: { fontSize: 16, fontWeight: '700', color: '#333' },
  progressBg: { height: 6, backgroundColor: '#e0e0e0', borderRadius: 3, overflow: 'hidden', marginVertical: 4 },
  progressFill: { height: '100%', borderRadius: 3 },
  devResumen: { flexDirection: 'row', justifyContent: 'space-around', marginBottom: 8 },
  devItem: { alignItems: 'center' },
  devValue: { fontSize: 18, fontWeight: '700' },
  devLabel: { fontSize: 11, color: '#888', marginTop: 2 },
});

export default ReporteEficienciaScreen;
